import Spinner from '@atlaskit/spinner';
import {
  AsyncResult,
  Environment,
  getIsBitbucket,
  RECAPTCHA_KEY,
  Url,
  useBitbucketWorkspaces
} from '@atlassiansox/bxpkit-core';
import styled from '@emotion/styled';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { SignupButtons } from '.';
import { SignupOptionsType } from '../../../../../model';
import { SignupLayoutAnalytics } from '../../../../../service';
import { SignUpFormView } from '../../sign-up-form';
import {
  BITBUCKET,
  onProductsRequireSitename
} from '../../utility/sign-up-layout-container';

import { SignUpFormUIStateProvider } from '../../../../../hook';
import { useAppSelector } from '../../../../../redux';
import { useOrgInfo } from '../../../../../hook/use-org-info';

/**
 * Obtains required data (aside from what's provided
 * by props), & render wac-signup-form component
 */

export function Signup({
  productKeys,
  userInfo,
  locale,
  migrationData,
  isMigrationSignup,
  analyticsTrack,
  setFormExistingSiteShown,
  allowEnableAutogenSiteName,
  allowSignupWithGoogle,
  allowSignupWithAllSocialOptions,
  enableFreeBreachTest,
  enableFieldProgression,
  fadeOnDisable,
  popOverError,
  outlineOnFocus,
  showWarningIcon,
  hideCaptchaEula,
  enableResendEmail,
  enableCarouselEndCard,
  firstImpressionsImageUrls,
  siteRenamePageUrl,
  enableSkipRecaptcha,
  hideGreeting = false
}: SignupOptionsType) {
  if (!userInfo || (isMigrationSignup && !migrationData)) {
    return (
      <SpinnerWrapper>
        <Spinner size="large" />
      </SpinnerWrapper>
    );
  }

  const [bbWorkspaces, setBbWorkspaces] = useState<any>();
  const isBitbucket = getIsBitbucket(productKeys);
  const makeRequest =
    !isBitbucket &&
    !!(
      !!userInfo.hasBitbucket &&
      userInfo?.email &&
      productKeys.some(({ product }) =>
        product.includes(BITBUCKET.toLowerCase())
      )
    );
  const bbWorkspacesAsync = useBitbucketWorkspaces({ makeRequest });
  const useSigninButtons =
    allowSignupWithGoogle && // prop
    !onProductsRequireSitename(productKeys) && // non-approved social signup bundle
    !_.get(userInfo, 'isLoggedIn'); // N2N only
  const { enableDefaultExpandedEmail } = useAppSelector(
    (state) => state.featureFlags
  );

  const [isEmailSignupSelected, setIsEmailSignupSelected] = useState(false);

  useEffect(() => {
    if (!bbWorkspacesAsync || AsyncResult.isPending(bbWorkspacesAsync)) {
      return;
    }
    const bbData = bbWorkspacesAsync.catch(() => null).value;
    if (bbData) {
      analytics.trackBbSignupPerf();
      const { values, size } = bbData;
      setBbWorkspaces({ size, values });
    }
  }, [bbWorkspacesAsync]);

  let userOrgInfo = null;
  if (isBitbucket) {
    userOrgInfo = useOrgInfo({
      includeOrgResponse: true,
      includeVortexMode: true
    });

    // continue rendering the spinner until the fetch completes
    if (!userOrgInfo.completed) {
      return (
        <SpinnerWrapper>
          <Spinner size="large" />
        </SpinnerWrapper>
      );
    }
  }

  const analytics = new SignupLayoutAnalytics({
    analyticsTrack,
    isLoggedIn: userInfo?.isLoggedIn || false
  });
  const { signInAccountUrl, switchAccountUrl, originAnalyticsAttributes } =
    analytics;

  const queryParameters = Url.getUrlParams() || {};
  const templateId = queryParameters.hasOwnProperty('templateId')
    ? queryParameters.templateId
    : null;
  const teamType = queryParameters.hasOwnProperty('team_type')
    ? queryParameters.team_type
    : null;
  const deferSiteNameCreation =
    !(userInfo && userInfo.isLoggedIn) ||
    (allowEnableAutogenSiteName &&
      !(userInfo && userInfo.isLoggedIn) &&
      !onProductsRequireSitename(productKeys));

  const hideCaptcha = allowSignupWithAllSocialOptions || !!hideCaptchaEula;

  const isConfluence =
    (productKeys ?? []).filter((item) => item.product.includes('confluence'))
      .length > 0;

  const signUpFormProps = {
    productKeys,
    templateId,
    showEmail: true,
    hideFullName: _.get(userInfo, 'isLoggedIn', false),
    userInfo,
    reCaptchaKey: RECAPTCHA_KEY,
    devMode: queryParameters.hasOwnProperty('developer'),
    marketingConsentRequired: _.get(
      locale,
      'localeRequiresMarketingCommunicationOptIn'
    ),
    locale: _.get(locale, 'locale'),
    migrationData: migrationData || undefined,
    isStaging: Environment.isStaging(),
    signInAccountUrl,
    switchAccountUrl: switchAccountUrl,
    originAnalyticsAttributes,
    wacEnvUrl: Environment.getWacIntegrationUrl(),
    analyticsTrack,
    bitbucketWorkspaces: bbWorkspaces,
    formExistingSiteShown: (val: boolean) => setFormExistingSiteShown(val),
    deferSiteNameCreation,
    enableFreeBreachTest,
    enableFieldProgression,
    fadeOnDisable,
    popOverError,
    outlineOnFocus,
    showWarningIcon,
    hideCaptchaEula: hideCaptcha,
    enableResendEmail,
    enableCarouselEndCard,
    firstImpressionsImageUrls,
    siteRenamePageUrl,
    enableSkipRecaptcha,
    hideGreeting,
    allowSignupWithAllSocialOptions,
    teamType,
    userOrgInfo
  };

  if (useSigninButtons) {
    if (allowSignupWithAllSocialOptions || isConfluence) {
      return (
        <>
          <SignUpFormUIStateProvider signUpFormProps={signUpFormProps}>
            <SignUpFormView {...signUpFormProps} />
          </SignUpFormUIStateProvider>
          <SignupButtons
            analyticsTrack={analyticsTrack}
            isEmailSignupSelected={isEmailSignupSelected}
            setIsEmailSignupSelected={setIsEmailSignupSelected}
            allowSignupWithAllSocialOptions={allowSignupWithAllSocialOptions}
          />
        </>
      );
    }

    return (
      <>
        <SignupButtons
          analyticsTrack={analyticsTrack}
          isEmailSignupSelected={isEmailSignupSelected}
          setIsEmailSignupSelected={setIsEmailSignupSelected}
        />

        {(isEmailSignupSelected || enableDefaultExpandedEmail) && (
          <SignUpFormUIStateProvider signUpFormProps={signUpFormProps}>
            <SignUpFormView {...signUpFormProps} />
          </SignUpFormUIStateProvider>
        )}
      </>
    );
  }

  return (
    <SignUpFormUIStateProvider signUpFormProps={signUpFormProps}>
      <SignUpFormView {...signUpFormProps} />
    </SignUpFormUIStateProvider>
  );
}

const SpinnerWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 260px;
  max-width: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
